import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { callAI, chatSystemPrompt } from '@/lib/ai';
import { X, Send, ChevronRight } from 'lucide-react';

const QUICK_REPLIES = [
  'Construction Estimate', 'Electrical Services',
  'Plumbing Works', 'Material Pricing', 'Call Now',
];

const WELCOME_MSG = "Hello, I'm Holistic \u2726 How can we help with your construction, electrical, plumbing, or material supply needs today? We're available 24 hours, Mon\u2013Sat.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: WELCOME_MSG, ts: new Date() },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [quickUsed, setQuickUsed] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text.trim(), ts: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setQuickUsed(true);
    setLoading(true);

    const history = [...messages, userMsg].map(m => ({ role: m.role, content: m.content }));
    const response = await callAI(chatSystemPrompt, history);
    setMessages(prev => [...prev, { role: 'assistant', content: response, ts: new Date() }]);
    setLoading(false);
  };

  return (
    <>
      {/* Trigger button */}
      <motion.button
        data-testid="chat-trigger"
        className="fixed bottom-6 right-6 w-[66px] h-[66px] rounded-full z-[500] flex items-center justify-center shadow-2xl"
        style={{
          background: 'radial-gradient(135deg, #E8A020, #B07818)',
          boxShadow: '0 8px 40px rgba(232,160,32,0.40)',
        }}
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.92 }}
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full animate-pulse-ring" style={{ border: '2px solid #E8A020' }} />
        {/* H icon */}
        <svg width="26" height="26" viewBox="0 0 80 80">
          <path d="M15 10 L15 70" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
          <path d="M65 10 L65 70" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
          <path d="M15 40 L65 40" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
        </svg>
        {/* Online badge */}
        <span className="absolute -top-1 -left-1 flex items-center gap-1 font-sans font-bold text-[9px] rounded-full px-2 py-0.5" style={{ background: '#10B981', color: '#fff' }}>
          24/7
        </span>
      </motion.button>

      {/* Widget panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget"
            className="fixed z-[500] right-6 rounded-2xl overflow-hidden flex flex-col"
            style={{
              bottom: '108px',
              width: '385px',
              height: '530px',
              maxHeight: 'calc(100vh - 140px)',
              background: '#0D1117',
              border: '1px solid rgba(232,160,32,0.20)',
            }}
            initial={{ scale: 0.72, opacity: 0, y: 32, transformOrigin: 'bottom right' }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.72, opacity: 0, y: 32 }}
            transition={{ type: 'spring', stiffness: 220, damping: 18, duration: 0.42 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4" style={{ background: 'linear-gradient(135deg, #141920, #1C2230)', borderBottom: '1px solid rgba(232,160,32,0.1)' }}>
              <div className="flex items-center gap-3">
                <svg width="18" height="18" viewBox="0 0 80 80">
                  <path d="M15 10 L15 70" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
                  <path d="M65 10 L65 70" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
                  <path d="M15 40 L65 40" stroke="#fff" strokeWidth="4" fill="none" strokeLinecap="round" />
                </svg>
                <div>
                  <p className="font-sans font-bold text-[15px] text-white">Holistic</p>
                  <p className="font-sans text-[11px]" style={{ color: '#E8A020' }}>Project Concierge</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5">
                  <motion.div
                    className="w-2 h-2 rounded-full"
                    style={{ background: '#10B981' }}
                    animate={{ scale: [1, 1.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <span className="font-sans text-[11px]" style={{ color: 'rgba(255,255,255,0.35)' }}>24/7 Online</span>
                </div>
                <button onClick={() => setOpen(false)} className="p-1" style={{ color: 'rgba(255,255,255,0.25)' }} data-testid="chat-close">
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(232,160,32,0.35) rgba(232,160,32,0.15)' }}>
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} gap-2`}>
                  {msg.role === 'assistant' && (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(232,160,32,0.1)' }}>
                      <svg width="14" height="14" viewBox="0 0 80 80">
                        <path d="M15 10 L15 70" stroke="#E8A020" strokeWidth="5" fill="none" strokeLinecap="round" />
                        <path d="M65 10 L65 70" stroke="#E8A020" strokeWidth="5" fill="none" strokeLinecap="round" />
                        <path d="M15 40 L65 40" stroke="#E8A020" strokeWidth="5" fill="none" strokeLinecap="round" />
                      </svg>
                    </div>
                  )}
                  <div className={`max-w-[75%] rounded-xl px-4 py-3 ${msg.role === 'user' ? 'rounded-tr-sm' : 'rounded-tl-sm'}`} style={{
                    background: msg.role === 'user' ? '#E8A020' : '#1C2230',
                    border: msg.role === 'assistant' ? '1px solid rgba(232,160,32,0.08)' : 'none',
                  }}>
                    <p className="font-sans text-sm leading-relaxed" style={{ color: msg.role === 'user' ? '#050608' : '#fff' }}>
                      {msg.content}
                    </p>
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(232,160,32,0.1)' }}>
                    <svg width="14" height="14" viewBox="0 0 80 80">
                      <path d="M15 10 L15 70" stroke="#E8A020" strokeWidth="5" fill="none" strokeLinecap="round" />
                      <path d="M65 10 L65 70" stroke="#E8A020" strokeWidth="5" fill="none" strokeLinecap="round" />
                      <path d="M15 40 L65 40" stroke="#E8A020" strokeWidth="5" fill="none" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className="rounded-xl rounded-tl-sm px-4 py-3 flex gap-1.5" style={{ background: '#1C2230' }}>
                    {[0, 1, 2].map(i => (
                      <motion.div
                        key={i}
                        className="w-2 h-2 rounded-full"
                        style={{ background: '#E8A020' }}
                        animate={{ scale: [1, 1.3, 1], opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Quick replies */}
            {!quickUsed && (
              <div className="px-4 pb-2 flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {QUICK_REPLIES.map(q => (
                  <button
                    key={q}
                    data-testid={`quick-reply-${q.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => q === 'Call Now' ? window.open('tel:+251911146950') : sendMessage(q)}
                    className="flex-shrink-0 font-sans font-medium text-xs rounded-full px-3 py-1.5 transition-all whitespace-nowrap"
                    style={{ border: '1px solid rgba(232,160,32,0.22)', color: 'rgba(255,255,255,0.6)' }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="px-4 py-3" style={{ borderTop: '1px solid rgba(232,160,32,0.08)', background: '#080A0D' }}>
              <div className="flex items-center gap-2">
                <input
                  data-testid="chat-input"
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendMessage(input)}
                  placeholder="Ask about our services..."
                  className="flex-1 font-sans text-sm text-white placeholder:text-white/30 rounded-full px-4 py-2.5 outline-none transition-all"
                  style={{ background: '#141920', border: '1px solid rgba(232,160,32,0.12)' }}
                />
                <button
                  data-testid="chat-send"
                  onClick={() => sendMessage(input)}
                  disabled={!input.trim() || loading}
                  className="w-[38px] h-[38px] rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                  style={{
                    background: input.trim() ? '#E8A020' : 'rgba(232,160,32,0.2)',
                    opacity: input.trim() ? 1 : 0.32,
                  }}
                >
                  <Send size={16} color="#050608" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
